import React, { useMemo, useState, useEffect } from 'react';
import { Chip, Stack } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import EditIcon from '@mui/icons-material/Edit';
import { alpha } from '@mui/material/styles';
import { useResources } from '@platform/core/src/context/ResourceContext';

const LocationEffectRenderer = ({ event }) => {
  const { change_type, resource_id, value, patches } = event;
  const { getResourceById } = useResources();
  const resource = getResourceById(resource_id);

  // Capture the name on first render before deletion removes it from context
  const [cachedName] = useState(() => {
    return resource?.name || value?.name || 'Location';
  });

  const changesToRender = useMemo(() => {
    const changes = [];
    const name = cachedName;

    // Handle Creation
    if (change_type === 'created') {
      changes.push({
        id: `loc-created-${resource_id}`,
        label: `New Location: ${name}`,
        icon: <PlaceIcon fontSize="small" />,
        color: "primary"
      });
    }
    // Handle Deletion
    else if (change_type === 'deleted') {
      changes.push({
        id: `loc-deleted-${resource_id}`,
        label: `Deleted Location: ${name}`,
        icon: <PlaceIcon fontSize="small" />,
        color: "error"
      });
    }
    // Handle Updates and Patches
    else if (change_type === 'updated' || change_type === 'patched') {
      const featurePatch = patches?.find(p => p.path === '/feature_ids');

      if (featurePatch) {
        const previousIds = Array.isArray(featurePatch.old_value) ? featurePatch.old_value : [];
        const newIds = Array.isArray(featurePatch.value) ? featurePatch.value : [];

        // Find actual additions (in new but not in previous)
        const added = newIds.filter(id => !previousIds.includes(id));
        added.forEach((id) => {
          const equipment = getResourceById(id);
          changes.push({
            id: `loc-added-${id}`,
            label: `+ ${equipment?.name || 'Equipment'}`,
            icon: <PlaceIcon fontSize="small" />,
            color: "success"
          });
        });

        // Find actual removals (in previous but not in new)
        const removed = previousIds.filter(id => !newIds.includes(id));
        removed.forEach((id) => {
          const equipment = getResourceById(id);
          changes.push({
            id: `loc-removed-${id}`,
            label: `- ${equipment?.name || 'Equipment'}`,
            icon: <PlaceIcon fontSize="small" />,
            color: "error"
          });
        });
      } else {
        // Fallback for generic name/description updates
        changes.push({
          id: `loc-upd-${resource_id}`,
          label: `Updated: ${name}`,
          icon: <EditIcon fontSize="small" />,
          color: "default"
        });
      }
    }

    return changes;
  }, [change_type, resource_id, cachedName, patches, getResourceById]);

  if (changesToRender.length === 0) return null;

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: '100%', my: 0.5 }}>
      {changesToRender.map((change) => (
        <Chip
          key={change.id}
          icon={change.icon}
          label={change.label}
          size="small"
          variant="outlined"
          color={change.color === 'default' ? 'default' : change.color}
          sx={{
            maxWidth: '100%',
            justifyContent: 'flex-start',
            bgcolor: (theme) => alpha(theme.palette[change.color === 'default' ? 'grey' : (change.color || 'primary')].main, 0.05),
            borderColor: (theme) => alpha(theme.palette[change.color === 'default' ? 'grey' : (change.color || 'primary')].main, 0.3),
          }}
        />
      ))}
    </Stack>
  );
};

LocationEffectRenderer.shouldRender = (event) => {
  return event.change_type === 'created' ||
         event.change_type === 'patched' ||
         event.change_type === 'updated' ||
         event.change_type === 'deleted';
};

export default LocationEffectRenderer;